import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Github,
  Sparkles,
  ZoomIn,
} from "lucide-react";
import PublicLayout from "../components/layout/PublicLayout";
import Lightbox from "../components/public/Lightbox";
import { usePortfolio } from "../contexts/PortfolioContext";
import { normalizeProjects } from "../utils/projectMedia";
import {
  DEFAULT_AUTHOR,
  DEFAULT_IMAGE,
  SITE_NAME,
  absoluteUrl,
  createBreadcrumbSchema,
  createProjectSchema,
  truncate,
} from "../utils/seo";

const splitParagraphs = (text) => {
  if (!text) return [];
  const blocks = text
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
  return blocks.length > 0 ? blocks : [text.trim()];
};

const ProjectPage = ({ isDownloading, handleDownload }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { portfolioData, loading } = usePortfolio();
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(0);

  const projects = useMemo(
    () => normalizeProjects(portfolioData.projects || []),
    [portfolioData.projects]
  );
  const project = projects.find((item) => item.slug === slug);
  const canonicalUrl = absoluteUrl(`/project/${slug}`);
  const pageTitle = project
    ? `${project.title} | Project by ${DEFAULT_AUTHOR}`
    : `Project not found | ${SITE_NAME}`;
  const description = project
    ? truncate(project.description, 155)
    : "This project case study could not be found in Aurio Rajaa's portfolio.";
  const imageUrl = absoluteUrl(project?.image || DEFAULT_IMAGE);
  const gallery = project?.gallery || [];
  const galleryLength = gallery.length;
  const activeMedia = gallery[activeIndex];
  const hasGallery = galleryLength > 1;
  const isFeatured =
    project?.featured === true || /featured/i.test(project?.status || "");
  const descriptionParagraphs = useMemo(
    () => splitParagraphs(project?.description),
    [project?.description]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug]);

  useEffect(() => {
    setActiveIndex(0);
  }, [slug]);

  const goPrev = useCallback(() => {
    if (!hasGallery) return;
    setActiveIndex((index) => (index - 1 + galleryLength) % galleryLength);
  }, [hasGallery, galleryLength]);

  const goNext = useCallback(() => {
    if (!hasGallery) return;
    setActiveIndex((index) => (index + 1) % galleryLength);
  }, [hasGallery, galleryLength]);

  const openZoom = (index) => {
    setZoomIndex(index);
    setZoomOpen(true);
  };

  const closeZoom = useCallback(() => setZoomOpen(false), []);

  const zoomPrev = useCallback(() => {
    if (!galleryLength) return;
    setZoomIndex((index) => (index - 1 + galleryLength) % galleryLength);
  }, [galleryLength]);

  const zoomNext = useCallback(() => {
    if (!galleryLength) return;
    setZoomIndex((index) => (index + 1) % galleryLength);
  }, [galleryLength]);

  if (loading) {
    return (
      <PublicLayout
        isDownloading={isDownloading}
        handleDownload={handleDownload}
      >
        <div className="public-loading">
          <div className="progress" aria-hidden="true">
            <div className="indeterminate" />
          </div>
          <p>Loading project…</p>
        </div>
      </PublicLayout>
    );
  }

  if (!project) {
    return (
      <PublicLayout
        isDownloading={isDownloading}
        handleDownload={handleDownload}
      >
        <Helmet>
          <title>{pageTitle}</title>
          <link rel="canonical" href={canonicalUrl} />
          <meta name="description" content={description} />
          <meta name="robots" content="noindex, follow" />
        </Helmet>

        <div className="container detail-page">
          <button
            type="button"
            className="detail-back"
            onClick={() => navigate("/")}
          >
            <ArrowLeft size={15} />
            Back to portfolio
          </button>
          <div className="project-empty">
            <h3>Project not found</h3>
            <p>This project may have moved or is no longer published.</p>
            <button
              type="button"
              className="btn"
              style={{ marginTop: "1rem" }}
              onClick={() => navigate("/")}
            >
              Back to portfolio
            </button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const breadcrumbs = createBreadcrumbSchema([
    { name: "Home", item: "/" },
    { name: "Projects", item: "/#projects" },
    { name: project.title, item: `/project/${project.slug}` },
  ]);
  const projectSchema = createProjectSchema(project, canonicalUrl);
  const metaTags = [
    project.role && { label: project.role, tone: "" },
    project.period && { label: project.period, tone: "" },
    project.status && { label: project.status, tone: "primary" },
  ].filter(Boolean);

  return (
    <PublicLayout
      isDownloading={isDownloading}
      handleDownload={handleDownload}
    >
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={description} />
        <meta name="author" content={DEFAULT_AUTHOR} />
        <meta
          name="keywords"
          content={[
            project.title,
            ...(project.tags || []),
            DEFAULT_AUTHOR,
            "software project",
            "portfolio",
          ].join(", ")}
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta
          property="og:image:alt"
          content={`${project.title} project preview`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(projectSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbs)}
        </script>
      </Helmet>

      <div className="container detail-page">
        <button
          type="button"
          className="detail-back"
          onClick={() =>
            navigate("/", { state: { scrollTo: "projects" } })
          }
        >
          <ArrowLeft size={15} />
          All projects
        </button>

        <header className="detail-header">
          <div className="detail-tags">
            {metaTags.map((tag) => (
              <span
                className={`public-chip${
                  tag.tone ? ` public-chip--${tag.tone}` : ""
                }`}
                key={tag.label}
              >
                {tag.label}
              </span>
            ))}
            {isFeatured && (
              <span className="public-chip public-chip--accent">Featured</span>
            )}
          </div>
          <h1 className="detail-title">{project.title}</h1>
          <p className="detail-description">
            {project.description
              ? truncate(project.description, 220)
              : "A closer look at the build, the stack, and what shipped."}
          </p>
          <div className="detail-actions">
            {project.github && (
              <a
                className="btn btn-outline"
                href={project.github}
                target="_blank"
                rel="noreferrer noopener"
              >
                <Github size={16} />
                Source code
              </a>
            )}
            {project.website && (
              <a
                className="btn"
                href={project.website}
                target="_blank"
                rel="noreferrer noopener"
              >
                <ExternalLink size={16} />
                Visit website
              </a>
            )}
          </div>
        </header>

        <div className="detail-grid">
          <div className="detail-main">
            <figure className="project-stage">
              {activeMedia ? (
                <>
                  <img
                    src={activeMedia.url}
                    alt={
                      activeMedia.alt || `${project.title} project preview`
                    }
                  />
                  <button
                    type="button"
                    className="project-stage-zoom"
                    aria-label="Open image in full size"
                    onClick={() => openZoom(activeIndex)}
                  >
                    <ZoomIn size={18} />
                  </button>
                </>
              ) : (
                <span className="project-stage-empty">
                  No preview available
                </span>
              )}
            </figure>

            {galleryLength > 0 && (
              <div>
                {hasGallery && (
                  <div className="project-gallery-controls">
                    <div className="gallery-arrows">
                      <button
                        type="button"
                        className="icon-button"
                        aria-label="Previous image"
                        onClick={goPrev}
                      >
                        <ChevronLeft size={17} />
                      </button>
                      <button
                        type="button"
                        className="icon-button"
                        aria-label="Next image"
                        onClick={goNext}
                      >
                        <ChevronRight size={17} />
                      </button>
                    </div>
                    <span className="gallery-count">
                      Image {activeIndex + 1} of {galleryLength}
                    </span>
                  </div>
                )}

                <div className="thumb-list">
                  {gallery.map((item, index) => (
                    <button
                      type="button"
                      className={`thumb${
                        index === activeIndex ? " is-active" : ""
                      }`}
                      key={item.id || item.url}
                      onClick={() => setActiveIndex(index)}
                      aria-label={`Show image ${index + 1}`}
                    >
                      <img
                        src={item.thumbnail || item.url}
                        alt={
                          item.alt ||
                          `${project.title} thumbnail ${index + 1}`
                        }
                        loading="lazy"
                        decoding="async"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="project-body">
              {descriptionParagraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}

              {project.highlights?.length > 0 && (
                <div className="highlight-card">
                  <h2>
                    <Sparkles size={17} />
                    Highlights
                  </h2>
                  <ul className="highlight-list">
                    {project.highlights.map((highlight, index) => (
                      <li key={`${project.id || project.slug}-highlight-${index}`}>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <aside className="detail-spec">
            <h2 className="detail-spec-title">Project details</h2>

            {project.role && (
              <div className="spec-row">
                <span className="spec-label">Role</span>
                <span className="spec-value">{project.role}</span>
              </div>
            )}
            {project.period && (
              <div className="spec-row">
                <span className="spec-label">Period</span>
                <span className="spec-value">{project.period}</span>
              </div>
            )}
            {project.status && (
              <div className="spec-row">
                <span className="spec-label">Status</span>
                <span className="spec-value">{project.status}</span>
              </div>
            )}
            {project.tags?.length > 0 && (
              <div className="spec-row">
                <span className="spec-label">Stack</span>
                <div className="chip-row" style={{ marginTop: "0.4rem" }}>
                  {project.tags.map((tag) => (
                    <span className="public-chip" key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              className="btn"
              onClick={() => navigate("/#contact")}
            >
              Start a conversation
            </button>
          </aside>
        </div>

        <div className="detail-end-note">
          Thanks for taking a closer look at this project.
        </div>
      </div>

      <Lightbox
        open={zoomOpen && galleryLength > 0}
        onClose={closeZoom}
        label={`${project.title} image preview`}
      >
        {galleryLength > 0 && (
          <>
            <div className="lightbox-head">
              <span className="lightbox-title">{project.title}</span>
              <span className="lightbox-count">
                {zoomIndex + 1} / {galleryLength}
              </span>
            </div>
            <div className="lightbox-image-wrap">
              <img
                src={gallery[zoomIndex]?.url}
                alt={
                  gallery[zoomIndex]?.alt ||
                  `${project.title} view ${zoomIndex + 1}`
                }
              />
            </div>
            <p className="lightbox-caption">
              {gallery[zoomIndex]?.alt || project.title}
            </p>
            {hasGallery && (
              <div className="lightbox-nav">
                <button
                  type="button"
                  className="icon-button"
                  aria-label="Previous image"
                  onClick={zoomPrev}
                >
                  <ChevronLeft size={17} />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  aria-label="Next image"
                  onClick={zoomNext}
                >
                  <ChevronRight size={17} />
                </button>
              </div>
            )}
          </>
        )}
      </Lightbox>
    </PublicLayout>
  );
};

export default ProjectPage;
